import React, { useState, useEffect, useCallback } from 'react';
import { Eye, EyeOff, X, User, Lock, AtSign } from 'lucide-react';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getAuthApiUrl } from '../lib/apiConfig';

const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function AuthModal({ isOpen, onClose }: AuthModalProps) {
  const { login, username: storedUsername } = useAuth(); // Get stored username
  const navigate = useNavigate(); // Add navigation hook
  const [isLogin, setIsLogin] = useState(true);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword2, setShowPassword2] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Pre-fill username field with stored username if available
  useEffect(() => {
    if (storedUsername && isLogin) {
      setUsername(storedUsername);
    } else if (!isLogin) {
      // Clear username for registration mode
      setUsername('');
    }
  }, [storedUsername, isLogin]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    // Client-side validation for registration
    if (!isLogin && password !== password2) {
      setError('Passwords do not match. Please make sure both password fields are identical.');
      setIsLoading(false);
      return;
    }

    try {
      if (isLogin) {
        // Login - use username field (which is pre-filled with stored username)
        console.log('Login data being sent:', {
          username: username,
          password: password,
        });
        
        const response = await fetch('/api/token-auth/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: username, // Use username field
            password: password,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          // Fetch user profile data
          const userResponse = await fetch('/api/users/me/', {
            headers: {
              'Authorization': `Token ${data.token}`,
            },
          });
          
          if (userResponse.ok) {
            const userData = await userResponse.json();
            login(data.token, userData).then(() => {
              onClose(); // Close the modal
              navigate('/discover'); // Navigate directly to swiping interface
            }).catch((error) => {
              console.error('Login error:', error);
              setError('Login failed');
            });
          } else {
            setError('Failed to fetch user profile');
          }
        } else {
          let errorData: { non_field_errors?: string[]; error?: string } = {};
          try {
            const text = await response.text();
            errorData = text ? JSON.parse(text) : {};
          } catch {
            setError(`Login failed (${response.status}). Please try again.`);
            return;
          }
          console.log('Login error response:', errorData);
          if (errorData.non_field_errors && errorData.non_field_errors.length > 0) {
            if (errorData.non_field_errors[0].includes('credentials')) {
              setError('Invalid username/email or password. Please check your credentials.');
            } else {
              setError(errorData.non_field_errors[0]);
            }
          } else if (errorData.error) {
            setError(errorData.error);
          } else {
            setError('Login failed. Please try again.');
          }
        }
      } else {
        // Registration logic
        console.log('Registration data being sent:', {
          username: username,
          email: email,
          password1: password,
          password2: password2,
        });

        const response = await fetch('/api/users/register/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: username,
            email: email,
            password1: password,
            password2: password2,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Registration successful:', data);
          
          // Auto-login after successful registration
          if (data.token) {
            console.log('Token received, fetching user profile...');
            // Fetch user profile data
            const userResponse = await fetch('/api/users/me/', {
              headers: {
                'Authorization': `Token ${data.token}`,
              },
            });
            
            if (userResponse.ok) {
              const userData = await userResponse.json();
              console.log('User profile fetched:', userData);
              login(data.token, userData).then(() => {
                onClose(); // Close the modal
                navigate('/discover'); // Navigate directly to swiping interface
              }).catch((error) => {
                console.error('Registration login error:', error);
                setError('Registration successful but login failed');
              });
            } else {
              console.error('Failed to fetch user profile:', userResponse.status);
              setError('Registration successful but failed to fetch user profile');
            }
          } else {
            console.error('No token in registration response:', data);
            setError('Registration successful but auto-login failed');
          }
        } else {
          let errorData: { errors?: Record<string, string[]>; message?: string } = {};
          try {
            const text = await response.text();
            errorData = text ? JSON.parse(text) : {};
          } catch {
            setError(`Registration failed (${response.status}). Please try again or use a different username/email.`);
            return;
          }
          console.log('Registration error response:', errorData);
          const errors: Record<string, string[]> = errorData.errors || {};
          if (errors.non_field_errors && errors.non_field_errors.length > 0) {
            setError(errors.non_field_errors[0]);
          } else if (errors.username && errors.username.length > 0) {
            setError(`Username: ${errors.username[0]}`);
          } else if (errors.email && errors.email.length > 0) {
            setError(`Email: ${errors.email[0]}`);
          } else if (errors.password1 && errors.password1.length > 0) {
            setError(`Password: ${errors.password1[0]}`);
          } else if (errors.password2 && errors.password2.length > 0) {
            setError(`Password confirmation: ${errors.password2[0]}`);
          } else if (typeof errorData.message === 'string') {
            setError(errorData.message);
          } else {
            setError('Registration failed. Please try again or use a different username/email.');
          }
        }
      }
    } catch (error) {
      console.error('Auth error:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setUsername('');
    setEmail('');
    setPassword('');
    setPassword2('');
    setError('');
    // Don't clear stored username, it should persist for convenience
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    resetForm();
    // Pre-fill username for login mode if available
    if (!isLogin && storedUsername) {
      setUsername(storedUsername);
    }
  };

  const handleGoogleSuccess = useCallback(
    async (cred: CredentialResponse) => {
      const credential = cred.credential;
      if (!credential) {
        setError('Google did not return a sign-in token. Please try again.');
        return;
      }
      setError('');
      setIsLoading(true);
      try {
        const response = await fetch(getAuthApiUrl('/api/auth/google/'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ credential }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          setError(
            typeof data.error === 'string'
              ? data.error
              : 'Google sign-in failed. Please try again or use email/password.'
          );
          return;
        }
        if (data.token && data.user) {
          await login(data.token, data.user);
          onClose();
          navigate('/discover');
        } else {
          setError('Unexpected response from server. Please try again.');
        }
      } catch (e) {
        console.error('Google auth error:', e);
        setError('Network error. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [login, navigate, onClose]
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="glass-strong rounded-3xl p-4 sm:p-8 max-w-md w-full relative my-8 max-h-[90vh] overflow-y-auto border-2 border-primary/20 shadow-2xl">
        {/* Gradient glow effect */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 rounded-3xl pointer-events-none" />

        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors z-[100] p-2 hover:bg-foreground/10 rounded-full"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header */}
        <div className="text-center mb-8 relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-gradient mb-2">
            {isLogin ? 'Welcome to Jaalu' : 'Join Jaalu'}
          </h2>
          <p className="text-muted-foreground text-lg">
            {isLogin ? 'Sign in to continue swiping' : 'Start your savings journey'}
          </p>
        </div>

        {googleClientId && (
          <div className="relative z-10 mb-6 flex flex-col items-center gap-3">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => setError('Google sign-in was cancelled or failed.')}
              useOneTap={false}
              theme="filled_blue"
              size="large"
              text="continue_with"
              shape="rectangular"
              width={320}
            />
            <p className="text-xs text-muted-foreground text-center px-2">
              {isLogin ? 'Or sign in with username and password' : 'Or create an account with email'}
            </p>
            <div className="flex items-center gap-3 w-full max-w-sm">
              <span className="flex-1 h-px bg-border" />
              <span className="text-xs text-muted-foreground">or</span>
              <span className="flex-1 h-px bg-border" />
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6 relative z-10">
          {/* Username (for both login and registration) */}
          <div>
            <label className="block text-sm font-medium mb-2 text-foreground">
              {isLogin ? 'Username or Email' : 'Username'}
            </label>
            <div className="relative">
              {/* User icon */}
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary" />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 sm:py-3 glass border-2 border-primary/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all text-base text-foreground placeholder:text-muted-foreground"
                placeholder={isLogin ? "Enter username or email" : "Choose a username"}
                required
              />
            </div>
            {isLogin && storedUsername && (
              <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                <span className="text-primary">💡</span>
                Your username is "<strong className="text-foreground">{storedUsername}</strong>" (pre-filled above)
              </p>
            )}
          </div>

          {/* Email (only for registration) */}
          {!isLogin && (
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Email</label>
              <div className="relative">
                {/* AtSign icon */}
                <AtSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-accent" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 sm:py-3 glass border-2 border-accent/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-accent focus:border-accent transition-all text-base text-foreground placeholder:text-muted-foreground"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>
          )}

          {/* Password */}
          <div>
            <label className="block text-sm font-medium mb-2 text-foreground">Password</label>
            <div className="relative">
              {/* Lock icon */}
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-secondary" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-12 py-2.5 sm:py-3 glass border-2 border-secondary/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary transition-all text-base text-foreground placeholder:text-muted-foreground"
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Confirm Password (only for registration) */}
          {!isLogin && (
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Confirm Password</label>
              <div className="relative">
                {/* Lock icon */}
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-secondary" />
                <input
                  type={showPassword2 ? 'text' : 'password'}
                  value={password2}
                  onChange={(e) => setPassword2(e.target.value)}
                  className="w-full pl-10 pr-12 py-2.5 sm:py-3 glass border-2 border-secondary/30 rounded-xl focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary transition-all text-base text-foreground placeholder:text-muted-foreground"
                  placeholder="Confirm your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword2(!showPassword2)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-primary transition-colors"
                >
                  {showPassword2 ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="text-red-400 text-sm text-center bg-red-400/10 border-2 border-red-400/30 rounded-xl p-4 backdrop-blur-sm animate-scale-in">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full btn-cosmic disabled:opacity-50 disabled:cursor-not-allowed py-3 sm:py-4 text-base font-semibold rounded-xl glow-primary hover:scale-105 transition-all"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {isLogin ? 'Signing In...' : 'Creating Account...'}
              </div>
            ) : (
              isLogin ? 'Sign In' : 'Create Account'
            )}
          </button>
        </form>

        {/* Toggle Mode */}
        <div className="text-center mt-6 relative z-10">
          <p className="text-muted-foreground">
            {isLogin ? "Don't have an account? " : "Already have an account? "}
            <button
              onClick={toggleMode}
              className="text-gradient font-semibold hover:opacity-80 transition-opacity"
            >
              {isLogin ? 'Sign up' : 'Sign in'}
            </button>
          </p>
        </div>

      </div>
    </div>
  );
};
